import { useEffect, useState, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { toast } from '../../lib/shadcn/sonner'
import MultiSelect from '../../components/MultiSelect'
import DatePicker from '../../components/DatePicker'
import { useUpdateProductsFieldsBulk, usePublishLabelProducts } from '../../hooks/backend/inventoryPage'
import { useGetProductOptions } from '../../hooks/backend/settingsPage'
import { publishLabels } from '../../utils/publishLabels'
import type { Product } from './ProductTable'

type PublishDestination = 'zoho' | 'n8n' | 'b2b' | 'web'

// Same names as the product dialog's Publish To.
const PUBLISH_OPTIONS: { label: string; value: PublishDestination }[] = [
  { label: 'Zoho', value: 'zoho' },
  { label: 'Faire', value: 'n8n' },
  { label: 'B2B', value: 'b2b' },
  { label: 'Web', value: 'web' },
]

const NO_TIER = '__none__'

// What updateProductsFieldsBulk takes; fields left out aren't changed.
type BulkParams = {
  skus: string[]
  publish_to?: PublishDestination[]
  categories_add?: string[]
  categories_remove?: string[]
  tags_add?: string[]
  tags_remove?: string[]
  is_seasonal?: boolean
  available_start?: string
  available_end?: string
  wholesale_tier?: string
  price?: number
}

interface ProductBulkEditDialogProps {
  products: Product[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

// One tick-box row: tick it (or change its value) to apply that field.
function FieldRow({
  id,
  label,
  checked,
  onCheckedChange,
  children,
  hint,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
  children: ReactNode
  hint?: ReactNode
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border p-3">
      <Checkbox id={id} checked={checked} onCheckedChange={v => onCheckedChange(!!v)} className="mt-0.5" />
      <div className="flex-1 min-w-0 space-y-2">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        {children}
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  )
}

export default function ProductBulkEditDialog({
  products,
  open,
  onOpenChange,
  onSaved,
}: ProductBulkEditDialogProps) {
  const updateBulk = useUpdateProductsFieldsBulk()
  const publishLabelProducts = usePublishLabelProducts()
  const productOptions = useGetProductOptions()
  const options = (productOptions.data as
    | { categories: string[]; tags: string[]; wholesaleTiers?: string[] }
    | undefined) ?? { categories: [], tags: [] }

  const [applyPublishTo, setApplyPublishTo] = useState(false)
  const [publishTo, setPublishTo] = useState<string[]>([])
  const [applyCategories, setApplyCategories] = useState(false)
  const [categoriesAdd, setCategoriesAdd] = useState<string[]>([])
  const [categoriesRemove, setCategoriesRemove] = useState<string[]>([])
  const [applyTags, setApplyTags] = useState(false)
  const [tagsAdd, setTagsAdd] = useState<string[]>([])
  const [tagsRemove, setTagsRemove] = useState<string[]>([])
  const [applySeasonal, setApplySeasonal] = useState(false)
  const [seasonal, setSeasonal] = useState<'on' | 'off'>('on')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [applyTier, setApplyTier] = useState(false)
  const [tier, setTier] = useState('')
  const [applyPrice, setApplyPrice] = useState(false)
  const [price, setPrice] = useState('')
  const [publish, setPublish] = useState(true)

  useEffect(() => {
    if (!open) return
    setApplyPublishTo(false)
    setPublishTo([])
    setApplyCategories(false)
    setCategoriesAdd([])
    setCategoriesRemove([])
    setApplyTags(false)
    setTagsAdd([])
    setTagsRemove([])
    setApplySeasonal(false)
    setSeasonal('on')
    setStart('')
    setEnd('')
    setApplyTier(false)
    setTier('')
    setApplyPrice(false)
    setPrice('')
    setPublish(true)
    void productOptions.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const n = products.length
  const anyTicked = applyPublishTo || applyCategories || applyTags || applySeasonal || applyTier || applyPrice
  const sharedTicked = applyPublishTo || applyCategories || applyTags || applySeasonal || applyTier

  // Keep the selected products' current values pickable even if they were
  // removed from Settings.
  const currentCategories = products.flatMap(p => p.categories ?? [])
  const currentTags = products.flatMap(p => p.tags ?? [])
  const categoryOptions = [...new Set([...options.categories, ...currentCategories])]
  const tagOptions = [...new Set([...options.tags, ...currentTags])]
  const removableCategories = [...new Set(currentCategories)].sort()
  const removableTags = [...new Set(currentTags)].sort()
  const tierOptions = [
    ...new Set([
      ...(options.wholesaleTiers ?? []),
      ...products.map(p => p.wholesale_tier ?? '').filter(Boolean),
    ]),
  ]

  const handleSave = async () => {
    if (n === 0) return
    if (!anyTicked) {
      toast.error('Tick at least one field to change')
      return
    }
    const params: BulkParams = { skus: products.map(p => p.sku) }
    if (applyPublishTo) {
      params.publish_to = PUBLISH_OPTIONS.filter(o => publishTo.includes(o.label)).map(o => o.value)
    }
    if (applyCategories) {
      if (categoriesAdd.length === 0 && categoriesRemove.length === 0) {
        toast.error('Pick categories to add or remove')
        return
      }
      params.categories_add = categoriesAdd
      params.categories_remove = categoriesRemove
    }
    if (applyTags) {
      if (tagsAdd.length === 0 && tagsRemove.length === 0) {
        toast.error('Pick tags to add or remove')
        return
      }
      params.tags_add = tagsAdd
      params.tags_remove = tagsRemove
    }
    if (applySeasonal) {
      if (seasonal === 'on') {
        if (!start || !end) {
          toast.error('Pick an Availability Start and End date')
          return
        }
        if (end < start) {
          toast.error('Availability End must be on or after Availability Start')
          return
        }
        params.is_seasonal = true
        params.available_start = start
        params.available_end = end
      } else {
        params.is_seasonal = false
      }
    }
    if (applyTier) {
      if (!tier) {
        toast.error('Pick a wholesale tier')
        return
      }
      params.wholesale_tier = tier === NO_TIER ? '' : tier
    }
    if (applyPrice) {
      const value = Number(price)
      if (price.trim() === '' || !Number.isFinite(value) || value < 0) {
        toast.error('Price must be a number, 0 or more')
        return
      }
      params.price = value
    }

    let res: { updated?: number; label_names?: string[]; no_label_skus?: string[] } | undefined
    try {
      res = (await updateBulk.trigger(params)) as typeof res
    } catch (error) {
      toast.error(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`)
      return
    }
    const count = res?.updated ?? n
    const labels = res?.label_names ?? []
    const noLabel = res?.no_label_skus ?? []
    onOpenChange(false)
    onSaved()
    if (!publish) {
      toast.success(`Updated ${count} product${count === 1 ? '' : 's'} in the Hub.`)
      return
    }
    toast.success(`Updated ${count} product${count === 1 ? '' : 's'} in the Hub. Publishing now…`)
    if (noLabel.length > 0) {
      toast.warning(
        `${noLabel.length} product${noLabel.length === 1 ? ' has' : 's have'} no Label Name, so only the Hub was updated. Save ${noLabel.length === 1 ? 'it' : 'them'} from its Edit window to publish.`,
        { duration: 15000 },
      )
    }
    await publishLabels(labels, p => publishLabelProducts.trigger(p))
  }

  const names = products.map(p => p.item_name || p.sku)
  const shownNames = names.slice(0, 8).join(', ') + (names.length > 8 ? `, and ${names.length - 8} more` : '')

  return (
    <Dialog open={open} onOpenChange={next => { if (!updateBulk.loading) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Edit Products</DialogTitle>
          <DialogDescription>
            Tick a field to apply it to all {n} selected product{n === 1 ? '' : 's'}. Fields left
            unticked aren't changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 flex-1 overflow-y-auto min-h-0">
          <p className="text-xs text-muted-foreground">{shownNames}</p>

          <FieldRow
            id="bulk-product-publish-to"
            label="Publish To"
            checked={applyPublishTo}
            onCheckedChange={setApplyPublishTo}
            hint="Replaces Publish To. Leave it empty to stop publishing them anywhere."
          >
            <MultiSelect
              options={PUBLISH_OPTIONS.map(o => o.label)}
              value={publishTo}
              onChange={v => { setPublishTo(v); setApplyPublishTo(true) }}
              placeholder="Select publishing destinations…"
              searchPlaceholder="Search destinations…"
            />
          </FieldRow>

          <FieldRow
            id="bulk-product-categories"
            label="Categories"
            checked={applyCategories}
            onCheckedChange={setApplyCategories}
            hint="Other categories each product already has stay as they are."
          >
            <MultiSelect
              options={categoryOptions}
              value={categoriesAdd}
              onChange={v => { setCategoriesAdd(v); setApplyCategories(true) }}
              placeholder="Add categories…"
              searchPlaceholder="Search categories…"
            />
            <MultiSelect
              options={removableCategories}
              value={categoriesRemove}
              onChange={v => { setCategoriesRemove(v); setApplyCategories(true) }}
              placeholder="Remove categories…"
              searchPlaceholder="Search categories…"
              emptyMessage="These products have no categories."
            />
          </FieldRow>

          <FieldRow
            id="bulk-product-tags"
            label="Tags"
            checked={applyTags}
            onCheckedChange={setApplyTags}
            hint="Other tags each product already has stay as they are."
          >
            <MultiSelect
              options={tagOptions}
              value={tagsAdd}
              onChange={v => { setTagsAdd(v); setApplyTags(true) }}
              placeholder="Add tags…"
              searchPlaceholder="Search tags…"
            />
            <MultiSelect
              options={removableTags}
              value={tagsRemove}
              onChange={v => { setTagsRemove(v); setApplyTags(true) }}
              placeholder="Remove tags…"
              searchPlaceholder="Search tags…"
              emptyMessage="These products have no tags."
            />
          </FieldRow>

          <FieldRow
            id="bulk-product-seasonal"
            label="Seasonal product"
            checked={applySeasonal}
            onCheckedChange={setApplySeasonal}
          >
            <Select
              value={seasonal}
              onValueChange={v => { setSeasonal(v as 'on' | 'off'); setApplySeasonal(true) }}
            >
              <SelectTrigger aria-label="Seasonal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on">Seasonal, available between these dates</SelectItem>
                <SelectItem value="off">Not seasonal (available all year)</SelectItem>
              </SelectContent>
            </Select>
            {seasonal === 'on' ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <DatePicker
                  id="bulk-product-start"
                  value={start}
                  placeholder="Pick start date"
                  onChange={v => { setStart(v); setApplySeasonal(true) }}
                />
                <DatePicker
                  id="bulk-product-end"
                  value={end}
                  defaultMonth={start}
                  placeholder="Pick end date"
                  onChange={v => { setEnd(v); setApplySeasonal(true) }}
                />
              </div>
            ) : null}
          </FieldRow>

          <FieldRow
            id="bulk-product-tier"
            label="Wholesale Tier"
            checked={applyTier}
            onCheckedChange={setApplyTier}
          >
            <Select value={tier} onValueChange={v => { setTier(v); setApplyTier(true) }}>
              <SelectTrigger aria-label="Wholesale tier">
                <SelectValue placeholder="Pick a tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TIER}>No tier</SelectItem>
                {tierOptions.map(t => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldRow>

          <FieldRow
            id="bulk-product-price"
            label="Price"
            checked={applyPrice}
            onCheckedChange={setApplyPrice}
            hint="Only the products you selected. Select just the Smalls or just the Larges to price one size."
          >
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={price}
              onChange={e => { setPrice(e.target.value); setApplyPrice(true) }}
              placeholder="e.g. 5.00"
              aria-label="Price"
            />
          </FieldRow>

          {sharedTicked ? (
            <p className="text-xs text-muted-foreground">
              Publish To, categories, tags, seasonal dates and wholesale tier are shared by Small
              and Large, so they change for every size of these flavors.
            </p>
          ) : null}

          <label className="flex items-start gap-3 pt-1 text-sm">
            <Checkbox checked={publish} onCheckedChange={v => setPublish(!!v)} className="mt-0.5" />
            <span>
              Publish the changes to each flavor&apos;s Publish To destinations (Zoho, Faire, B2B,
              Web) after saving
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updateBulk.loading}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={updateBulk.loading || !anyTicked}>
            {updateBulk.loading ? 'Saving…' : `Update ${n}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
